import {
    useEffect,
    useState,
    useMemo,
    type FC,
    useRef,
    useId
} from "react";
import {
    type LayoutChangeEvent,
    useWindowDimensions,
    Animated,
    Platform,
    View
} from "react-native";
import type IResizablePanelGroupProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useStableCallback
} from "../../../../helpers/interaction";
import {
    useReducedMotion,
    getMotionTiming
} from "../../../../helpers/motion";
import type {
    ResizableStoredLayoutType,
    ResizableDragConstantType,
    ResizableStepConstantType,
    ResizableEdgeConstantType,
    ResizableHandleStateType,
    ResizableContextType,
    ResizableMode
} from "../../type";
import {
    RESIZABLE_SHIFT_MULTIPLIER,
    RESIZABLE_PERCENT_TOTAL,
    parseResizableChildren,
    RESIZABLE_EPSILON,
    getInitialSizes,
    getPanelConfig,
    resolveRelease,
    resolveLayout,
    getPercents,
    getBounds,
    clamp,
    sum
} from "../../util";
import {
    readNativeLayout,
    readWebLayout,
    writeLayout
} from "../../storage";
import {
    ResizableIndexContext,
    ResizableContext
} from "../../context";
import Tabs from "../../../tabs";

const ResizablePanelGroup: FC<IResizablePanelGroupProps> = ({
    compactBreakpoint: compactBreakpointProp,
    compactBehaviour = "stack",
    orientation = "horizontal",
    persistKey = null,
    isFramed = true,
    customLocalize,
    onLayoutChange,
    customTheme,
    children,
    style,
    ...props
}) => {
    const {
        controlHeights,
        shapeConfigs,
        durations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isReduceMotionEnabled = useReducedMotion();

    const {
        width: windowWidth
    } = useWindowDimensions();

    const baseID = useId();

    const {
        entries,
        panels
    } = parseResizableChildren(children);

    const panelCount = panels.length;

    const handleCount = entries.filter((entry) => entry.kind === "handle" && entry.index >= 0 && entry.index < panelCount - 1).length;

    const [
        initialStored
    ] = useState<ResizableStoredLayoutType | undefined>(() => persistKey ? readWebLayout(persistKey) : undefined);

    const [
        isStorageLoaded,
        setIsStorageLoaded
    ] = useState(() => Platform.OS === "web" || !persistKey);

    const [
        collapsed,
        setCollapsed
    ] = useState<Array<boolean>>([]);

    const [
        available,
        setAvailable
    ] = useState(0);

    const [
        sizes,
        setSizes
    ] = useState<Array<number>>([]);

    const [
        isReady,
        setIsReady
    ] = useState(false);

    const [
        tab,
        setTab
    ] = useState(initialStored?.tab ?? "0");

    const animationRef = useRef<Animated.CompositeAnimation | undefined>(undefined);

    const dragRef = useRef< |
    {
        sizes: Array<number>;
        index: number;
    } |
    undefined>(undefined);

    const storedRef = useRef<ResizableStoredLayoutType | undefined>(initialStored);

    const collapsedRef = useRef<Array<boolean>>([]);

    const restoreRef = useRef<Array<number>>([]);

    const sizesRef = useRef<Array<number>>([]);

    const compactBreakpoint = compactBreakpointProp ?? shapeConfigs.compactBreakpoint;

    const mode: ResizableMode = windowWidth < compactBreakpoint ? compactBehaviour : "split";

    const collapsedFallback = orientation === "horizontal" && mode === "split" ? controlHeights.large + spaces.spacingLg : controlHeights.medium;

    const configs = panels.map((panel) => getPanelConfig({
        collapsedSize: panel.props.collapsedSize,
        isCollapsible: panel.props.isCollapsible,
        defaultSize: panel.props.defaultSize,
        maxSize: panel.props.maxSize,
        minSize: panel.props.minSize,
        collapsedFallback,
        available
    }));

    const configsRef = useRef(configs);

    configsRef.current = configs;

    const panelsRef = useRef(panels);

    panelsRef.current = panels;

    const animatedSizes = useMemo(() => Array.from({
        length: panelCount
    }, () => new Animated.Value(0)), [
        panelCount
    ]);

    const {
        inner: innerDynamicStyle,
        root: rootDynamicStyle
    } = useStyles({
        shapeConfigs,
        orientation,
        isFramed,
        radiuses,
        borders,
        colors,
        spaces,
        mode
    });

    const handleStopAnimation = () => {
        animationRef.current?.stop();

        animationRef.current = undefined;
    };

    const handlePersist = (tabValue: string) => {
        if(!persistKey) {
            return;
        }

        writeLayout(persistKey, {
            percents: getPercents(sizesRef.current, available),
            collapsed: collapsedRef.current,
            tab: tabValue
        });
    };

    const handleApplyLayout = ({
        collapsed: nextCollapsed,
        isAnimate = false,
        isPersist = false,
        isNotify = false,
        sizes: nextSizes
    }: {
        collapsed: Array<boolean>;
        sizes: Array<number>;
        isAnimate?: boolean;
        isPersist?: boolean;
        isNotify?: boolean;
    }) => {
        handleStopAnimation();

        const previousCollapsed = collapsedRef.current;

        sizesRef.current = nextSizes;

        collapsedRef.current = nextCollapsed;

        setCollapsed(nextCollapsed);

        setSizes(nextSizes);

        const isCollapsing = nextCollapsed.some((isCollapsed, index) => isCollapsed && !previousCollapsed[index]);

        const {
            duration: timingDuration,
            easing
        } = getMotionTiming({
            intent: isCollapsing ? "collapse" : "expand",
            isReduceMotionEnabled,
            durations
        });

        const duration = isAnimate ? timingDuration : 0;

        if(!duration) {
            animatedSizes.forEach((value, index) => {
                value.setValue(nextSizes[index] ?? 0);
            });
        }

        if(duration) {
            animationRef.current = Animated.parallel(animatedSizes.map((value, index) => Animated.timing(value, {
                toValue: nextSizes[index] ?? 0,
                useNativeDriver: false,
                duration,
                easing
            })));

            animationRef.current.start();
        }

        if(isPersist) {
            handlePersist(tab);
        }

        if(!isNotify) {
            return;
        }

        onLayoutChange?.(getPercents(nextSizes, available));

        nextCollapsed.forEach((isCollapsed, index) => {
            const panel = panelsRef.current[index];

            if(isCollapsed === previousCollapsed[index] || panel?.props.isCollapsed !== undefined) {
                return;
            }

            panel?.props.onCollapsedChange?.(isCollapsed);
        });
    };

    const getCollapseLayout = (index: number, isCollapse: boolean) => {
        const config = configs[index];

        if(!config) {
            return undefined;
        }

        const nextCollapsed = [...collapsedRef.current];

        const nextSizes = [...sizesRef.current];

        const current = nextSizes[index] ?? 0;

        if(isCollapse) {
            restoreRef.current[index] = current;

            nextSizes[index] = config.collapsedPx;
        }

        if(!isCollapse) {
            nextSizes[index] = clamp(restoreRef.current[index] ?? config.defaultPx ?? available / panelCount, config.minPx, config.maxPx);
        }

        nextCollapsed[index] = isCollapse;

        const neighbor = index + 1 < panelCount ? index + 1 : index - 1;

        if(neighbor >= 0) {
            nextSizes[neighbor] = (nextSizes[neighbor] ?? 0) + current - (nextSizes[index] ?? 0);
        }

        return {
            sizes: resolveLayout({
                pinned: nextSizes.map((_, order) => order === index),
                collapsed: nextCollapsed,
                sizes: nextSizes,
                available,
                configs
            }),
            collapsed: nextCollapsed
        };
    };

    const handleRequestCollapse = (index: number, isCollapse: boolean) => {
        const panel = panels[index];

        if(!panel) {
            return;
        }

        if(panel.props.isCollapsed !== undefined) {
            panel.props.onCollapsedChange?.(isCollapse);

            return;
        }

        const layout = getCollapseLayout(index, isCollapse);

        if(!layout) {
            return;
        }

        handleApplyLayout({
            isAnimate: true,
            isPersist: true,
            isNotify: true,
            ...layout
        });
    };

    const handleMoveTo = (index: number, desired: number) => {
        const bounds = getBounds({
            isIncludeCollapse: false,
            sizes: sizesRef.current,
            configs,
            index
        });

        const prevSize = clamp(desired, bounds.low, bounds.high);

        const nextSizes = [...sizesRef.current];

        nextSizes[index] = prevSize;

        nextSizes[index + 1] = bounds.total - prevSize;

        handleApplyLayout({
            collapsed: collapsedRef.current.map((isCollapsed, order) => order === index || order === index + 1 ? false : isCollapsed),
            sizes: nextSizes,
            isAnimate: true,
            isPersist: true,
            isNotify: true
        });
    };

    const handleStep = useStableCallback(({
        keyboardStep,
        direction,
        isShift,
        index
    }: ResizableStepConstantType) => {
        if(collapsedRef.current[index] || collapsedRef.current[index + 1]) {
            const target = collapsedRef.current[index] ? index : index + 1;

            const isExpand = target === index ? direction > 0 : direction < 0;

            if(isExpand) {
                handleRequestCollapse(target, false);
            }

            return;
        }

        const step = available * keyboardStep / RESIZABLE_PERCENT_TOTAL * (isShift ? RESIZABLE_SHIFT_MULTIPLIER : 1);

        handleMoveTo(index, (sizesRef.current[index] ?? 0) + direction * step);
    });

    const handleEdge = useStableCallback(({
        edge,
        index
    }: ResizableEdgeConstantType) => {
        if(collapsedRef.current[index] || collapsedRef.current[index + 1]) {
            return;
        }

        if(edge === "start" && configs[index]?.isCollapsible) {
            handleRequestCollapse(index, true);

            return;
        }

        if(edge === "end" && configs[index + 1]?.isCollapsible) {
            handleRequestCollapse(index + 1, true);

            return;
        }

        handleMoveTo(index, edge === "start" ? 0 : available);
    });

    const handleToggleCollapse = useStableCallback((index: number) => {
        const target = configs[index]?.isCollapsible ? index : configs[index + 1]?.isCollapsible ? index + 1 : undefined;

        if(target === undefined) {
            return;
        }

        handleRequestCollapse(target, !collapsedRef.current[target]);
    });

    const handleReset = useStableCallback((index: number) => {
        if(collapsedRef.current[index]) {
            handleRequestCollapse(index, false);

            return;
        }

        const initial = getInitialSizes({
            collapsed: configs.map(() => false),
            available,
            configs
        });

        handleMoveTo(index, configs[index]?.defaultPx ?? initial[index] ?? 0);
    });

    const handleDragStart = useStableCallback((index: number) => {
        handleStopAnimation();

        dragRef.current = {
            sizes: [...sizesRef.current],
            index
        };
    });

    const getDragSizes = ({
        delta,
        index
    }: ResizableDragConstantType): Array<number> | undefined => {
        const start = dragRef.current;

        if(!start || start.index !== index) {
            return undefined;
        }

        const bounds = getBounds({
            sizes: start.sizes,
            configs,
            index
        });

        const prevSize = clamp((start.sizes[index] ?? 0) + delta, bounds.low, bounds.high);

        const nextSizes = [...start.sizes];

        nextSizes[index] = prevSize;

        nextSizes[index + 1] = bounds.total - prevSize;

        return nextSizes;
    };

    const handleDragMove = useStableCallback(({
        delta,
        index
    }: ResizableDragConstantType) => {
        const nextSizes = getDragSizes({
            delta,
            index
        });

        if(!nextSizes) {
            return;
        }

        sizesRef.current = nextSizes;

        animatedSizes[index]?.setValue(nextSizes[index] ?? 0);

        animatedSizes[index + 1]?.setValue(nextSizes[index + 1] ?? 0);
    });

    const handleDragEnd = useStableCallback(({
        delta,
        index
    }: ResizableDragConstantType) => {
        const start = dragRef.current;

        dragRef.current = undefined;

        if(!start || start.index !== index) {
            return;
        }

        const release = resolveRelease({
            desired: (start.sizes[index] ?? 0) + delta,
            sizes: start.sizes,
            configs,
            index
        });

        const nextCollapsed = collapsedRef.current.map((isCollapsed, order) => release.collapsed[order] ?? isCollapsed);

        const isSame = release.sizes.every((size, order) => Math.abs(size - (start.sizes[order] ?? 0)) < RESIZABLE_EPSILON) && nextCollapsed.every((isCollapsed, order) => isCollapsed === collapsedRef.current[order]);

        if(isSame) {
            handleApplyLayout({
                collapsed: collapsedRef.current,
                sizes: start.sizes
            });

            return;
        }

        const controlledIndex = nextCollapsed.findIndex((isCollapsed, order) => isCollapsed !== collapsedRef.current[order] && panels[order]?.props.isCollapsed !== undefined);

        if(controlledIndex >= 0) {
            handleApplyLayout({
                collapsed: collapsedRef.current,
                sizes: start.sizes,
                isAnimate: true
            });

            panels[controlledIndex]?.props.onCollapsedChange?.(!!nextCollapsed[controlledIndex]);

            return;
        }

        nextCollapsed.forEach((isCollapsed, order) => {
            if(isCollapsed && !collapsedRef.current[order]) {
                restoreRef.current[order] = start.sizes[order] ?? 0;
            }
        });

        handleApplyLayout({
            collapsed: nextCollapsed,
            sizes: release.sizes,
            isAnimate: true,
            isPersist: true,
            isNotify: true
        });
    });

    const getHandleState = useStableCallback((index: number): ResizableHandleStateType => {
        const config = configs[index];

        const size = sizes[index] ?? 0;

        const bounds = getBounds({
            configs,
            sizes,
            index
        });

        const title = panels[index]?.props.title;

        const getPercent = (value: number) => Math.round(available > 0 ? value / available * RESIZABLE_PERCENT_TOTAL : 0);

        const state: ResizableHandleStateType = {
            label: title ? localize(orientation === "horizontal" ? "resizable-width" : "resizable-height", [title]) : localize("resizable-handle"),
            controls: `${baseID}-panel-${index}`,
            valueMax: getPercent(bounds.high),
            valueMin: getPercent(bounds.low),
            valueNow: getPercent(size)
        };

        if(collapsed[index]) {
            state.valueText = localize("resizable-pixels-collapsed", [Math.round(size)]);
        }

        if(!collapsed[index] && config?.isFixed) {
            state.valueText = localize("resizable-pixels", [Math.round(size)]);
        }

        if(bounds.high - bounds.low < RESIZABLE_EPSILON) {
            return state;
        }

        if(size - bounds.low < RESIZABLE_EPSILON) {
            state.limit = "min";
        }

        if(bounds.high - size < RESIZABLE_EPSILON) {
            state.limit = "max";
        }

        return state;
    });

    const handleTabChange = (value: string) => {
        setTab(value);

        handlePersist(value);
    };

    const onLayout = (event: LayoutChangeEvent) => {
        const {
            height,
            width
        } = event.nativeEvent.layout;

        setAvailable(Math.max(0, (orientation === "horizontal" ? width : height) - handleCount * borders.separator));
    };

    useEffect(() => {
        if(Platform.OS === "web" || !persistKey) {
            return;
        }

        let isActive = true;

        readNativeLayout(persistKey).then((layout) => {
            if(!isActive) {
                return;
            }

            storedRef.current = layout;

            if(layout?.tab) {
                setTab(layout.tab);
            }

            setIsStorageLoaded(true);
        });

        return () => {
            isActive = false;
        };
    }, [
        persistKey
    ]);

    useEffect(() => {
        if(mode !== "split" || available <= 0 || !isStorageLoaded || !panelCount) {
            return;
        }

        if(sizesRef.current.length === panelCount) {
            const resolved = resolveLayout({
                collapsed: collapsedRef.current,
                sizes: sizesRef.current,
                available,
                configs
            });

            if(Math.abs(sum(resolved) - sum(sizesRef.current)) < RESIZABLE_EPSILON && resolved.every((size, index) => Math.abs(size - (sizesRef.current[index] ?? 0)) < RESIZABLE_EPSILON)) {
                return;
            }

            handleApplyLayout({
                collapsed: collapsedRef.current,
                sizes: resolved
            });

            return;
        }

        const stored = storedRef.current;

        const isStoredValid = !!stored && stored.percents.length === panelCount && stored.collapsed.length === panelCount;

        const initialCollapsed = panels.map((panel, index) => panel.props.isCollapsed ?? (isStoredValid ? !!stored.collapsed[index] : !!panel.props.isDefaultCollapsed));

        handleApplyLayout({
            sizes: isStoredValid ? resolveLayout({
                sizes: stored.percents.map((percent) => percent * available / RESIZABLE_PERCENT_TOTAL),
                collapsed: initialCollapsed,
                available,
                configs
            }) : getInitialSizes({
                collapsed: initialCollapsed,
                available,
                configs
            }),
            collapsed: initialCollapsed
        });

        setIsReady(true);
    }, [
        isStorageLoaded,
        panelCount,
        available,
        mode
    ]);

    const controlledKey = panels.map((panel) => panel.props.isCollapsed === undefined ? "-" : panel.props.isCollapsed ? "1" : "0").join("");

    useEffect(() => {
        if(!isReady || mode !== "split") {
            return;
        }

        panels.forEach((panel, index) => {
            const value = panel.props.isCollapsed;

            if(value === undefined || value === collapsedRef.current[index]) {
                return;
            }

            const layout = getCollapseLayout(index, value);

            if(!layout) {
                return;
            }

            handleApplyLayout({
                isAnimate: true,
                isPersist: true,
                isNotify: true,
                ...layout
            });
        });
    }, [
        controlledKey,
        isReady
    ]);

    useEffect(() => {
        return () => {
            animationRef.current?.stop();
        };
    }, []);

    const collapsedValue = mode === "split" ? collapsed : panels.map((panel) => panel.props.isCollapsed ?? !!panel.props.isDefaultCollapsed);

    const collapsedKey = collapsedValue.join();

    const collapsedSizesValue = configs.map((config) => config.collapsedPx);

    const collapsedSizesKey = collapsedSizesValue.join();

    const panelIDs = useMemo(() => Array.from({
        length: panelCount
    }, (_, index) => `${baseID}-panel-${index}`), [
        panelCount,
        baseID
    ]);

    const currentCollapsed = useMemo(() => collapsedValue, [
        collapsedKey
    ]);

    const currentCollapsedSizes = useMemo(() => collapsedSizesValue, [
        collapsedSizesKey
    ]);

    const context = useMemo<ResizableContextType>(() => ({
        collapsedSizes: currentCollapsedSizes,
        collapsed: currentCollapsed,
        handleToggleCollapse,
        handleDragStart,
        customLocalize,
        getHandleState,
        handleDragMove,
        animatedSizes,
        handleDragEnd,
        customTheme,
        handleReset,
        orientation,
        handleEdge,
        handleStep,
        panelCount,
        isFramed,
        panelIDs,
        isReady,
        mode
    }), [
        currentCollapsedSizes,
        currentCollapsed,
        handleToggleCollapse,
        handleDragStart,
        customLocalize,
        getHandleState,
        handleDragMove,
        animatedSizes,
        handleDragEnd,
        customTheme,
        handleReset,
        orientation,
        handleEdge,
        handleStep,
        panelCount,
        panelIDs,
        isFramed,
        isReady,
        mode,
        available,
        localize,
        sizes
    ]);

    const renderEntries = () => entries.map((entry) => {
        if(entry.kind === "handle" && (mode !== "split" || entry.index < 0 || entry.index >= panelCount - 1)) {
            return null;
        }

        return <ResizableIndexContext.Provider
            key={entry.element.key}
            value={entry.index}
        >
            {entry.element}
        </ResizableIndexContext.Provider>;
    });

    const renderTabs = () => {
        return <Tabs
            onValueChange={handleTabChange}
            customLocalize={customLocalize}
            spreadBehaviour="stretch"
            customTheme={customTheme}
            variant="segment"
            value={tab}
        >
            <Tabs.List
                title={localize("resizable-panels")}
                customTheme={customTheme}
            >
                {panels.map((panel, index) => <Tabs.Trigger
                    title={panel.props.title ?? localize("resizable-panel", [
                        index + 1
                    ])}
                    customLocalize={customLocalize}
                    customTheme={customTheme}
                    icon={panel.props.icon}
                    value={String(index)}
                    key={panel.key}
                />)}
            </Tabs.List>
            {renderEntries()}
        </Tabs>;
    };

    const renderContent = () => {
        if(mode === "tabs") {
            return renderTabs();
        }

        return <View
            onLayout={mode === "split" ? onLayout : undefined}
            style={[
                stylesheet.inner,
                innerDynamicStyle
            ]}
        >
            {renderEntries()}
        </View>;
    };

    return <ResizableContext.Provider
        value={context}
    >
        <View
            {...props}
            style={[
                style,
                mode === "split" ? stylesheet.root : stylesheet.rootAuto,
                rootDynamicStyle
            ]}
        >
            {renderContent()}
        </View>
    </ResizableContext.Provider>;
};
export default ResizablePanelGroup;
